import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from '@bangle.io/base-ui';
import React from 'react';

export type DialogSingleInputProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
  onSelect: (input: string) => void;
  title?: string;
  description?: string;
  inputLabel?: string;
  submitText?: string;
  placeholder?: string;
  Icon?: React.ElementType;
  initialSearch?: string;
  /** When true, Enter submits even with an empty value (e.g. keep default name). */
  allowEmptySubmit?: boolean;
  /** Called when the dialog closes without a successful submit (Esc, Cancel, …). */
  onDismiss?: () => void;
};

export function DialogSingleInput({
  open,
  setOpen,
  onSelect,
  placeholder = t.app.dialogs.singleInput.placeholderDefault,
  title,
  description,
  inputLabel,
  submitText = t.app.common.continueButton,
  Icon,
  initialSearch = '',
  allowEmptySubmit = false,
  onDismiss,
}: DialogSingleInputProps) {
  const [search, setSearch] = React.useState(initialSearch);
  const submittedRef = React.useRef(false);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const inputId = React.useId();
  const dialogTitle = title || inputLabel || placeholder;
  const dialogDescription = description || placeholder;

  React.useEffect(() => {
    setSearch(initialSearch);
  }, [initialSearch]);

  React.useEffect(() => {
    if (!open) {
      return;
    }

    window.requestAnimationFrame(() => {
      const input = inputRef.current;
      if (!input) {
        return;
      }
      input.focus({ preventScroll: true });
      input.select();
    });
  }, [open]);

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      if (!submittedRef.current) {
        onDismiss?.();
      }
      submittedRef.current = false;
    }
    setOpen(nextOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="sm:max-w-md"
        // Keep the app visible — no dim/blur wash behind rename/create prompts.
        overlayClassName="bg-transparent supports-backdrop-filter:backdrop-blur-none"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {Icon && <Icon className="h-5 w-5" aria-hidden="true" />}
            <span>{dialogTitle}</span>
          </DialogTitle>
          <DialogDescription>{dialogDescription}</DialogDescription>
        </DialogHeader>

        <form
          className="grid gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            if (!allowEmptySubmit && !search.trim()) {
              return;
            }

            submittedRef.current = true;
            onSelect(search);
            setOpen(false);
          }}
        >
          <div className="grid gap-2">
            {inputLabel ? <Label htmlFor={inputId}>{inputLabel}</Label> : null}
            <Input
              id={inputId}
              ref={inputRef}
              placeholder={placeholder}
              aria-label={inputLabel ?? placeholder}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
            >
              {t.app.common.cancelButton}
            </Button>
            <Button
              type="submit"
              disabled={!allowEmptySubmit && !search.trim()}
            >
              {submitText}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
